import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Experiment,
  ExperimentVariant,
  ExperimentState,
  CallState,
} from "redmond";
import { fetchActiveExperiments } from "../api/v0/experiments/fetchExperiments";

// Active Experiments
// export const EXAMPLE = "c1-example";

export const TRAVEL_WALLET_CREDITS_EXPERIMENT =
  "c1-marketplace-travel-wallet-credits";
export const CREDIT_OFFER_STACKING_V1 =
  "c1-marketplace-credit-offer-stacking-v1";
export const TRAVEL_CREDIT_HISTORY_EXPERIMENT =
  "c1-marketplace-credit-transaction-history";
export const ANNUAL_TRAVEL_CREDITS = "c1-marketplace-annual-travel-credits";
export const C1_AUTO_SELECT_CARD_AT_CHECKOUT =
  "c1-auto-select-card-at-checkout";
export const TRAVEL_SALE = "c1-marketplace-travel-sale";
export const TREES_MODAL_EXPERIMENT = "c1-marketplace-trees";
export const ACTIVITY_CROSS_SELL_EXPERIMENT = "c1-agency-experiences-xsell";
export const ACTIVITIES_SURVEY = "c1-activities-survey";

export const EXPERIENCES_SEARCH_NEARBY_EXPERIMENT =
  "c1-agency-experiences-search-nearby";
export const XMOD_V1_EXPERIMENT = "c1-marketplace-xmod-v1";
export const TRAVEL_APP_GTM_EXPERIMENT = "c1-portex-app-gtm";
export const DATELESS_SEARCH_EXPERIMENT = "c1-activities-dateless-search";
export const COMBINED_SEARCH_EXPERIMENT = "c1-activities-combined-search";
export const COMBINED_SEARCH_IMAGES_EXPERIMENT =
  "c1-activities-combined-search-images";
export const CHECKOUT_IMPROVEMENTS_EXPERIMENT =
  "c1-activities-checkout-improvements";
export const CHECKOUT_DEFAULT_COMM_EXPERIMENT =
  "c1-activities-checkout-default-comm";

// Variants for c1-activities-combined-search-images
export const COMBINED_SEARCH_IMAGES_VARIANT_IMAGES = "images";
export const COMBINED_SEARCH_IMAGES_VARIANT_ICONS = "icons";
export const COMBINED_SEARCH_IMAGES_VARIANT_BOTH = "both";
export const COMBINED_SEARCH_IMAGES_VARIANTS = [
  "control" as const,
  COMBINED_SEARCH_IMAGES_VARIANT_IMAGES,
  COMBINED_SEARCH_IMAGES_VARIANT_ICONS,
  COMBINED_SEARCH_IMAGES_VARIANT_BOTH,
] as const;

// Default variants
export const CONTROL = "control";
export const AVAILABLE = "available";

// Variants for c1-marketplace-travel-sale
export const TRAVEL_SALE_LEAD_UP = "lead-up";
export const TRAVEL_SALE_ACTIVE = "active";
export const TRAVEL_SALE_VARIANTS = [
  CONTROL,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_ACTIVE,
] as const;

// Variants for c1-agency-experiences-xsell
export const ACTIVITIES_XSELL = "activities-xsell";
export const STAYS_AND_ACTIVITIES_XSELL = "stays-and-activities-xsell";
export const STAYS_MANUAL_SORT_XSELL = "stays-manual-sort-xsell";
export const ACTIVITY_CROSS_SELL_EXPERIMENT_VARIANTS = [
  CONTROL,
  AVAILABLE,
  ACTIVITIES_XSELL,
  STAYS_AND_ACTIVITIES_XSELL,
  STAYS_MANUAL_SORT_XSELL,
] as const;

const defaultInitializer = (): ExperimentStateWithCallState => ({
    experiments: [],
    trackingProperties: undefined,
    callState: CallState.NotCalled,
  });

export const ExperimentsContext = createContext<ExperimentState | undefined>(
  undefined
);

// readonly (together with const restriction below) ensures typeof supportedVariants[number]
// can be recognized by transpiler as an array of constants
// [string, ...string[]] ensures array is not empty.
export function getExperimentVariantCustomVariants<
  T extends readonly [string, ...string[]]
>(
  experiments: Array<Experiment>,
  experimentId: string,
  // List of supported variants. If the variant retreived is not recognized by the code, the code will fall back to the first variant.
  // Note the input list needs to be declared as const e.g. const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
  supportedVariants: T
): (typeof supportedVariants)[number] {
  const experiment = experiments?.find((exp) => exp.id === experimentId);

  if (experiment) {
    if (supportedVariants.includes(experiment.variant)) {
      return experiment.variant;
    } 
      return supportedVariants[0];
    
  } 
    return supportedVariants[0];
  
}

export function getExperimentVariant(
  experiments: Array<Experiment> = [], // eslint-disable-line default-param-last
  // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
  experimentId: String,
  defaultExperimentVariant: ExperimentVariant = ExperimentVariant.CONTROL  
): ExperimentVariant {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }
    return null;
  });

  return (experiment?.variant as ExperimentVariant) ?? defaultExperimentVariant;
}

export const isCheckoutImprovementsEnabled = (
  experiments?: Array<Experiment>
): boolean =>
  getExperimentVariant(experiments, CHECKOUT_IMPROVEMENTS_EXPERIMENT) === AVAILABLE;

interface ExperimentStateWithCallState extends ExperimentState {
  callState: CallState;
}

export function useExperiments(): ExperimentState {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) throw new Error(`must be used within a ExperimentsProvider`);
  return ctx;
}

export function addTrackingProperties(
  trackingProperties: Map<string, string> | undefined,
  properties?: any // eslint-disable-line @typescript-eslint/no-explicit-any
): unknown {
  if (!properties) {
    properties = {}; // eslint-disable-line no-param-reassign
  }
  if (trackingProperties) {
    properties.experiments = Object.keys(trackingProperties).map( // eslint-disable-line no-param-reassign
      (ex) => `${ex}_${trackingProperties![ex]}`
    );
  }
  return properties;
}

export enum ActiveExperiments {}

const ExperimentsProvider: FC<
  PropsWithChildren<{ initState?: ExperimentStateWithCallState }>
> = ({ initState = defaultInitializer(), children }) => {
  // TODO: use tracking reducers
  const [state, setState] = useState(initState);

  useEffect(() => {
    if (!initState?.experiments.length) {
      const fetchExperiments = async () => {
        setState({ ...state, callState: CallState.InProcess });
        await fetchActiveExperiments().then((result) => {
          setState({
            ...(result as ExperimentState),
            callState: CallState.Success,
          });
        });
      };
      fetchExperiments();
    }
  }, []);

  return (
    <ExperimentsContext.Provider value={state}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export default ExperimentsProvider;
